import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { shuffle, standardDeck, type Card } from '@campfire/cards';
import { ACE_LOW_RUNS, bestArrangement, type Arrangement, type Layoff } from '@campfire/melds';

export interface GinOptions {
  /** Most deadwood you may knock with; 0 = gin only. */
  readonly knock: number;
  /** Points to win the game. */
  readonly target: number;
}

export const GIN_BONUS = 25;
export const UNDERCUT_BONUS = 25;

/** How a hand ended, with both hands laid out for everyone to check. */
export interface GinHandResult {
  readonly hand: number;
  readonly knocker: Seat;
  readonly gin: boolean;
  readonly knockerMelds: readonly (readonly Card[])[];
  readonly knockerDeadwood: readonly Card[];
  readonly knockerPoints: number;
  readonly defenderMelds: readonly (readonly Card[])[];
  /** Defender cards laid off on the knocker's melds (target = index into knockerMelds). */
  readonly layoffs: readonly Layoff[];
  readonly defenderDeadwood: readonly Card[];
  readonly defenderPoints: number;
  readonly winner: Seat;
  readonly points: number;
  readonly undercut: boolean;
}

export type GinEvent =
  /** `card` is shown only when taken from the discard pile. */
  | { readonly kind: 'draw'; readonly seat: Seat; readonly from: 'stock' | 'discard'; readonly card: Card | null }
  | { readonly kind: 'discard'; readonly seat: Seat; readonly card: Card }
  | { readonly kind: 'hand'; readonly result: GinHandResult }
  | { readonly kind: 'washed'; readonly hand: number };

export interface GinState {
  readonly options: GinOptions;
  readonly hands: readonly (readonly Card[])[];
  /** Index 0 is the top. */
  readonly stock: readonly Card[];
  /** Index 0 is the face-up top card. */
  readonly discard: readonly Card[];
  readonly turn: Seat;
  readonly phase: 'draw' | 'discard';
  /** The card just taken from the discard pile; it may not go straight back. */
  readonly took: string | null;
  readonly dealer: Seat;
  readonly hand: number;
  readonly scores: readonly number[];
  /** The most recent finished hand, kept so both players can study it. */
  readonly lastHand: GinHandResult | null;
  readonly last: GinEvent | null;
  readonly result: Outcome | null;
}

export type GinAction =
  | { readonly type: 'draw'; readonly from: 'stock' | 'discard' }
  | { readonly type: 'discard'; readonly card: string }
  /** Discard `card` face down and lay down your hand. */
  | { readonly type: 'knock'; readonly card: string };

export interface GinView {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  /** Your best melds and deadwood right now. */
  readonly arrangement: Arrangement | null;
  /** Cards you may discard to knock (empty unless it's your discard). */
  readonly knockable: readonly string[];
  readonly handCounts: readonly number[];
  readonly discard: readonly Card[];
  readonly stockCount: number;
  readonly turn: Seat;
  readonly phase: 'draw' | 'discard';
  readonly took: string | null;
  readonly dealer: Seat;
  readonly handNo: number;
  readonly scores: readonly number[];
  readonly knockLimit: number;
  readonly target: number;
  readonly lastHand: GinHandResult | null;
  readonly last: GinEvent | null;
  readonly result: Outcome | null;
}

const arrange = (cards: readonly Card[], layoffOnto?: readonly (readonly Card[])[]) =>
  bestArrangement(cards, layoffOnto ? { rules: ACE_LOW_RUNS, layoffOnto } : { rules: ACE_LOW_RUNS });

function deal(options: GinOptions, dealer: Seat, hand: number, scores: readonly number[], lastHand: GinHandResult | null, rng: Rng): GinState {
  const deck = shuffle(standardDeck(), rng);
  return {
    options,
    hands: [deck.slice(0, 10), deck.slice(10, 20)],
    discard: [deck[20] as Card],
    stock: deck.slice(21),
    turn: 1 - dealer,
    phase: 'draw',
    took: null,
    dealer,
    hand,
    scores,
    lastHand,
    last: null,
    result: null,
  };
}

/** Score a knock (FR-GIN-04..06). `hand` is the knocker's 10 cards after the discard. */
export function scoreKnock(knocker: Seat, knockerHand: readonly Card[], defenderHand: readonly Card[], handNo = 1): GinHandResult {
  const k = arrange(knockerHand);
  const gin = k.points === 0;
  // No laying off against gin.
  const d = gin ? arrange(defenderHand) : arrange(defenderHand, k.melds);
  const defender = 1 - knocker;
  const undercut = !gin && d.points <= k.points;
  const winner = undercut ? defender : knocker;
  const points = gin ? d.points + GIN_BONUS : undercut ? k.points - d.points + UNDERCUT_BONUS : d.points - k.points;
  return {
    hand: handNo,
    knocker,
    gin,
    knockerMelds: k.melds,
    knockerDeadwood: k.deadwood,
    knockerPoints: k.points,
    defenderMelds: d.melds,
    layoffs: d.layoffs,
    defenderDeadwood: d.deadwood,
    defenderPoints: d.points,
    winner,
    points,
    undercut,
  };
}

export const gin: GameDefinition<GinState, GinAction, GinView, GinOptions> = {
  id: 'gin',
  name: 'Gin Rummy',
  rules: [
    'Two players. Form your 10 cards into sets (3 or 4 of a kind) and runs (3 or more in a suit); the cards left over are your deadwood. Ace is always low, so Q-K-A is not a run.',
    'Each player is dealt 10 cards and one card is turned up to start the discard pile. The player who is not the dealer goes first and may take that card or draw from the stock.',
    'A turn is: draw one card (from the stock, or the top of the discard pile), then discard one. You may not discard the card you just took from the pile.',
    'Deadwood counts ace 1, faces 10, other cards face value. The app finds your best arrangement for you and shows your deadwood.',
    'To end the hand, discard face down and Knock. Setting: Knocking. By default you need 10 or less deadwood; Gin only means you must have none. Going out with no deadwood is Gin.',
    'After a knock the opponent lays off cards onto your sets and runs (the app does this for them), then scores the difference. No laying off against Gin.',
    'Knocker wins the difference in deadwood. Gin scores the opponent’s deadwood plus a 25 bonus. If the opponent’s deadwood is equal or lower, that is an undercut: they score the difference plus a 25 bonus.',
    'If the stock gets down to two cards with no knock, the hand is a wash: nobody scores and the same dealer deals again.',
    'Setting: Game length. First to 100 or 250 points wins. There is no big gin, no line bonus and no box bonus.',
    'House rules the app does not enforce — agree on them at the table: for example, some groups require at least 50 points of melds before anyone may lay down.',
  ],
  minSeats: 2,
  maxSeats: 2,
  defaultOptions: { knock: 10, target: 100 },
  optionSpecs: [
    {
      key: 'knock',
      label: 'Knocking',
      help: 'Deadwood is the total of cards not in a set or run (ace 1, faces 10).',
      choices: [
        { value: 10, label: 'Knock with 10 or less deadwood' },
        { value: 0, label: 'Gin only' },
      ],
    },
    {
      key: 'target',
      label: 'Game length',
      choices: [
        { value: 100, label: 'First to 100 points' },
        { value: 250, label: 'First to 250 points' },
      ],
    },
  ],

  setup({ options, rng }) {
    return deal(options, 0, 1, [0, 0], null, rng);
  },

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    if (a.type === 'draw') {
      if (s.phase !== 'draw') return { ok: false, reason: 'you have drawn — now discard' };
      if (a.from !== 'stock' && a.from !== 'discard') return { ok: false, reason: 'draw from the stock or the discard pile' };
      return { ok: true };
    }
    if (a.type !== 'discard' && a.type !== 'knock') return { ok: false, reason: 'unknown action' };
    if (s.phase !== 'discard') return { ok: false, reason: 'draw a card first' };
    const hand = s.hands[seat] ?? [];
    if (!hand.some((c) => c.id === a.card)) return { ok: false, reason: 'you do not hold that card' };
    if (a.card === s.took) return { ok: false, reason: 'you can’t discard the card you just took' };
    if (a.type === 'knock' && arrange(hand.filter((c) => c.id !== a.card)).points > s.options.knock) {
      return { ok: false, reason: s.options.knock === 0 ? 'you need gin (no deadwood) to go down' : `you need ${s.options.knock} or less deadwood to knock` };
    }
    return { ok: true };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'draw') {
      const fromDiscard = a.from === 'discard';
      const card = (fromDiscard ? s.discard[0] : s.stock[0]) as Card;
      return {
        ...s,
        hands: s.hands.map((h, i) => (i === seat ? [...h, card] : h)),
        stock: fromDiscard ? s.stock : s.stock.slice(1),
        discard: fromDiscard ? s.discard.slice(1) : s.discard,
        phase: 'discard',
        took: fromDiscard ? card.id : null,
        last: { kind: 'draw', seat, from: a.from, card: fromDiscard ? card : null },
      };
    }
    const card = (s.hands[seat] as readonly Card[]).find((c) => c.id === a.card) as Card;
    const hands = s.hands.map((h, i) => (i === seat ? h.filter((c) => c.id !== card.id) : h));
    const after: GinState = { ...s, hands, discard: [card, ...s.discard], phase: 'draw', took: null, turn: 1 - seat };
    if (a.type === 'knock') {
      const result = scoreKnock(seat, hands[seat] as Card[], hands[1 - seat] as Card[], s.hand);
      const scores = s.scores.map((v, i) => (i === result.winner ? v + result.points : v));
      const last: GinEvent = { kind: 'hand', result };
      if ((scores[result.winner] ?? 0) >= s.options.target) {
        return { ...after, scores, lastHand: result, last, result: { winners: [result.winner], draw: false, reason: result.gin ? 'gin' : 'knock', scores: { ...scores } } };
      }
      return { ...deal(s.options, 1 - s.dealer, s.hand + 1, scores, result, rng), last };
    }
    // Down to the last two cards of the stock without a knock: nobody scores, same dealer deals again.
    if (after.stock.length <= 2) return { ...deal(s.options, s.dealer, s.hand + 1, s.scores, s.lastHand, rng), last: { kind: 'washed', hand: s.hand } };
    return { ...after, last: { kind: 'discard', seat, card } };
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const hand = you === null ? null : (s.hands[you] ?? null);
    const mine = you !== null && you === s.turn && !s.result && s.phase === 'discard';
    const knockable =
      mine && hand ? hand.filter((c) => gin.validate(s, { type: 'knock', card: c.id }, you).ok).map((c) => c.id) : [];
    return {
      you,
      hand,
      arrangement: hand ? arrange(hand) : null,
      knockable,
      handCounts: s.hands.map((h) => h.length),
      discard: s.discard,
      stockCount: s.stock.length,
      turn: s.turn,
      phase: s.phase,
      took: s.took,
      dealer: s.dealer,
      handNo: s.hand,
      scores: s.scores,
      knockLimit: s.options.knock,
      target: s.options.target,
      lastHand: s.lastHand,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    if (s.phase === 'draw') return [{ type: 'draw', from: 'stock' }, { type: 'draw', from: 'discard' }];
    // Sensible play for bots and fuzzing: every knock, but discards only from deadwood (never break
    // a meld) unless the whole hand is melded. Every listed action is legal.
    const hand = s.hands[seat] ?? [];
    const dead = new Set(arrange(hand).deadwood.map((c) => c.id));
    const free = hand.filter((c) => c.id !== s.took);
    const pool = free.some((c) => dead.has(c.id)) ? free.filter((c) => dead.has(c.id)) : free;
    const out: GinAction[] = pool.map((c) => ({ type: 'discard', card: c.id }));
    for (const c of free) if (gin.validate(s, { type: 'knock', card: c.id }, seat).ok) out.push({ type: 'knock', card: c.id });
    return out;
  },
};
